import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Library.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Colors for roles
const ROLE_COLORS = {
  opener: '#22c55e',
  closer: '#22c55e',
  slow: '#ec4899',
  sprint: '#eab308',
  arms: '#06b6d4',
  climb: '#f97316',
  jog: '#3b82f6',
  flat: '#8b5cf6',
  cooldown: '#94a3b8',
  warmup: '#fb923c',
  energy_high: '#ef4444'
};

const ROLE_ICONS = {
  opener: '🎬',
  closer: '🏁',
  slow: '🫠',
  sprint: '⚡️',
  arms: '💪',
  climb: '⛰️',
  jog: '🏃',
  flat: '🛣️',
  cooldown: '🧊',
  warmup: '🔥',
  energy_high: '🤘'
};

function Library() {
  const { user } = useAuth();
  const [view, setView] = useState('collections'); // 'roles', 'collections', 'roleDetail', 'collectionDetail', 'byPosition', 'dashDetail'
  const [roles, setRoles] = useState([]);
  const [collections, setCollections] = useState([]);
  const [dashPlaylists, setDashPlaylists] = useState([]);
  const [dashDetail, setDashDetail] = useState(null);
  const [byPosition, setByPosition] = useState(null);
  const [expandedPositions, setExpandedPositions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [, setSelectedCollection] = useState(null); // eslint: unused read is intentional
  const [roleDetail, setRoleDetail] = useState(null);
  const [collectionDetail, setCollectionDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: 'last_played', direction: 'desc' });
  const [hideRecent, setHideRecent] = useState(false);
  const [hideAfterColorado, setHideAfterColorado] = useState(false);
  const [showOneHitWonders, setShowOneHitWonders] = useState(false);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentList, setCurrentList] = useState([]);
  const [currentListContext, setCurrentListContext] = useState(null);
  
  // June 2022 - when Matt moved to Colorado
  const coloradoDate = new Date('2022-06-01');

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  const filterRecent = (songs) => {
    if (!songs) return songs;
    let filtered = songs;
    
    // Hide songs played in last 6 months
    if (hideRecent) {
      filtered = filtered.filter(song => {
        if (!song.last_played) return true;
        return new Date(song.last_played) < sixMonthsAgo;
      });
    }
    
    // Hide songs with > 1 play since Colorado move (June 2022)
    // This hides songs that have been played multiple times in CO
    if (hideAfterColorado) {
      filtered = filtered.filter(song => {
        // If never played, keep it
        if (!song.last_played) return true;
        // If played only once, keep it (might have been the first CO play)
        if ((song.play_count || 0) <= 1) return true;
        // If last played before CO move, keep it
        if (new Date(song.last_played) < coloradoDate) return true;
        // Otherwise hide - it's been played multiple times since CO
        return false;
      });
    }
    
    // One Hit Wonders - only show songs played exactly once since mid-2022
    if (showOneHitWonders) {
      filtered = filtered.filter(song => {
        // Use plays_since_colorado if available, otherwise fall back to play_count check
        const playsSinceColorado = parseInt(song.plays_since_colorado) || 0;
        return playsSinceColorado === 1;
      });
    }
    
    return filtered;
  };

  const getSortedSongs = (songs) => {
    if (!songs) return [];
    const filtered = filterRecent(songs);
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.field) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'artist':
          aVal = ((a.artist_names || [])[0] || '').toLowerCase();
          bVal = ((b.artist_names || [])[0] || '').toLowerCase();
          break;
        case 'bpm':
          aVal = a.bpm || 0;
          bVal = b.bpm || 0;
          break;
        case 'play_count':
          aVal = a.play_count || 0;
          bVal = b.play_count || 0;
          break;
        case 'last_played':
          aVal = a.last_played ? new Date(a.last_played).getTime() : 0;
          bVal = b.last_played ? new Date(b.last_played).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filterRecentTracks = (tracks) => {
    if (!tracks) return tracks;
    let filtered = tracks;
    
    // Hide tracks played in last 6 months
    if (hideRecent) {
      filtered = filtered.filter(track => {
        if (!track.last_played) return true;
        return new Date(track.last_played) < sixMonthsAgo;
      });
    }
    
    // Hide tracks with > 1 play since Colorado move (June 2022)
    if (hideAfterColorado) {
      filtered = filtered.filter(track => {
        if (!track.last_played) return true;
        if ((track.play_count || 0) <= 1) return true;
        if (new Date(track.last_played) < coloradoDate) return true;
        return false;
      });
    }
    
    // One Hit Wonders - only show tracks played exactly once since mid-2022
    if (showOneHitWonders) {
      filtered = filtered.filter(track => {
        // Use plays_since_colorado if available
        const playsSinceColorado = parseInt(track.plays_since_colorado) || 0;
        return playsSinceColorado === 1;
      });
    }
    
    return filtered;
  };

  const getSortedCollectionTracks = (tracks) => {
    if (!tracks) return [];
    const filtered = filterRecentTracks(tracks);
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.field) {
        case 'position':
          aVal = a.position || 0;
          bVal = b.position || 0;
          break;
        case 'name':
          aVal = (a.track_name || '').toLowerCase();
          bVal = (b.track_name || '').toLowerCase();
          break;
        case 'artist':
          aVal = (a.artist_name || '').toLowerCase();
          bVal = (b.artist_name || '').toLowerCase();
          break;
        case 'bpm':
          aVal = a.bpm || 0;
          bVal = b.bpm || 0;
          break;
        case 'play_count':
          aVal = a.play_count || 0;
          bVal = b.play_count || 0;
          break;
        case 'last_played':
          aVal = a.last_played ? new Date(a.last_played).getTime() : 0;
          bVal = b.last_played ? new Date(b.last_played).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  useEffect(() => {
    fetchRoles();
    fetchCollections();
    fetchDashPlaylists();
  }, []);

  // Poll playback state to detect when song finishes
  useEffect(() => {
    if (!isPlaying || !currentPlayingId || currentList.length === 0) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_BASE}/playback/state`, { withCredentials: true });
        const state = response.data;

        // Check if the track changed or stopped
        if (!state.isPlaying || !state.track) {
          // Song finished, play next
          const currentIndex = currentList.findIndex(song =>
            (song.spotify_id || song.spotify_track_id) === currentPlayingId
          );

          if (currentIndex >= 0 && currentIndex < currentList.length - 1) {
            const nextSong = currentList[currentIndex + 1];
            const nextSpotifyId = nextSong.spotify_id || nextSong.spotify_track_id;
            if (nextSpotifyId) {
              await playSong(nextSpotifyId, currentList, currentListContext);
            }
          } else {
            // End of list
            setIsPlaying(false);
            setCurrentPlayingId(null);
          }
        } else if (state.track) {
          // Update current playing track if changed
          const trackId = state.track.id;
          if (trackId !== currentPlayingId) {
            setCurrentPlayingId(trackId);
          }
        }
      } catch (err) {
        console.error('Error polling playback state:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [isPlaying, currentPlayingId, currentList, currentListContext]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_BASE}/generator/library/roles`, {
        withCredentials: true
      });
      setRoles(response.data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API_BASE}/generator/library/collections`, {
        withCredentials: true
      });
      setCollections(response.data);
    } catch (err) {
      console.error('Failed to fetch collections:', err);
    }
  };

  const fetchDashPlaylists = async () => {
    try {
      const response = await axios.get(`${API_BASE}/generator/library/dash-playlists`, {
        withCredentials: true
      });
      setDashPlaylists(response.data);
    } catch (err) {
      console.error('Failed to fetch dash playlists:', err);
    }
  };

  const fetchByPosition = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/songs/by-position`, {
        withCredentials: true
      });
      setByPosition(response.data);
      setView('byPosition');
    } catch (err) {
      console.error('Failed to fetch by position:', err);
    } finally {
      setLoading(false);
    }
  };

  const viewDashDetail = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/generator/library/dash-playlist/${id}`, {
        withCredentials: true
      });
      setDashDetail(response.data);
      setView('dashDetail');
    } catch (err) {
      console.error('Failed to fetch dash playlist detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePosition = (key) => {
    setExpandedPositions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const stripDashes = (name) => {
    return name.replace(/^-\s*/, '').replace(/\s*-$/, '').trim();
  };

  const playSong = async (spotifyId, songList, context) => {
    try {
      const trackUri = `spotify:track:${spotifyId}`;
      const response = await axios.post(`${API_BASE}/playback/play`, {
        uris: [trackUri]
      }, { withCredentials: true });
      if (response.data.success) {
        setCurrentPlayingId(spotifyId);
        setIsPlaying(true);
        setCurrentList(songList);
        setCurrentListContext(context);
      }
    } catch (err) {
      console.error('Error playing song:', err);
      alert(err.response?.data?.message || 'Failed to play song');
    }
  };

  const pauseSong = async () => {
    try {
      const response = await axios.post(`${API_BASE}/playback/pause`, {}, { withCredentials: true });
      if (response.data.success) {
        setIsPlaying(false);
      }
    } catch (err) {
      console.error('Error pausing song:', err);
    }
  };

  const handlePlayPause = async (spotifyId, songList, context) => {
    if (currentPlayingId === spotifyId && isPlaying) {
      await pauseSong();
    } else if (currentPlayingId === spotifyId && !isPlaying) {
      // Resume current song
      try {
        const response = await axios.post(`${API_BASE}/playback/play`, {}, { withCredentials: true });
        if (response.data.success) {
          setIsPlaying(true);
        }
      } catch (err) {
        console.error('Error resuming song:', err);
      }
    } else {
      await playSong(spotifyId, songList, context);
    }
  };

  const viewRoleDetail = async (role) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/generator/library/role/${role}`, {
        withCredentials: true
      });
      setRoleDetail(response.data);
      setSelectedRole(role);
      setView('roleDetail');
    } catch (err) {
      console.error('Failed to fetch role detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const viewCollectionDetail = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/generator/library/collection/${id}`, {
        withCredentials: true
      });
      setCollectionDetail(response.data);
      setSelectedCollection(id);
      setView('collectionDetail');
    } catch (err) {
      console.error('Failed to fetch collection detail:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="library">
      <div className="library-header">
        <h1>📚 Curated Library</h1>
        <div className="library-tabs">
          <button 
            className={`tab ${view === 'roles' || view === 'roleDetail' ? 'active' : ''}`}
            onClick={() => setView('roles')}
          >
            🏷️ By Role
          </button>
          <button 
            className={`tab ${view === 'collections' || view === 'collectionDetail' || view === 'dashDetail' ? 'active' : ''}`}
            onClick={() => setView('collections')}
          >
            📋 Collections
          </button>
          <button 
            className={`tab ${view === 'byPosition' ? 'active' : ''}`}
            onClick={() => fetchByPosition()}
          >
            📍 By Position
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading...</div>}

      {/* ROLES OVERVIEW */}
      {view === 'roles' && (
        <div className="roles-grid">
          {roles.map(role => (
            <div 
              key={role.role} 
              className="role-card"
              onClick={() => viewRoleDetail(role.role)}
              style={{ borderColor: ROLE_COLORS[role.role] || '#666' }}
            >
              <div className="role-icon">{ROLE_ICONS[role.role] || '🎵'}</div>
              <div className="role-name">{role.role}</div>
              <div className="role-count">{role.song_count} songs</div>
              <div className="role-bpm">
                {role.min_bpm}-{role.max_bpm} BPM
                <span className="avg-bpm">(avg {role.avg_bpm})</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ROLE DETAIL */}
      {view === 'roleDetail' && roleDetail && (
        <div className="detail-view">
          <div className="detail-header">
            <button className="back-btn" onClick={() => setView('roles')}>
              ← Back to Roles
            </button>
            <div className="filter-checkboxes">
              <label className="hide-recent-checkbox">
                <input 
                  type="checkbox" 
                  checked={hideRecent} 
                  onChange={(e) => setHideRecent(e.target.checked)}
                />
                Hide Recent
              </label>
              {user?.isInstructor && (
                <label className="hide-recent-checkbox hide-colorado">
                  <input 
                    type="checkbox" 
                    checked={hideAfterColorado} 
                    onChange={(e) => setHideAfterColorado(e.target.checked)}
                  />
                  Colorado: Songs not played since 2022
                </label>
              )}
              {user?.isInstructor && (
                <label className="hide-recent-checkbox one-hit-wonders">
                  <input 
                    type="checkbox" 
                    checked={showOneHitWonders} 
                    onChange={(e) => setShowOneHitWonders(e.target.checked)}
                  />
                  Colorado: Songs played only once since 2022
                </label>
              )}
            </div>
          </div>
          <h2>
            {ROLE_ICONS[selectedRole]} {selectedRole} 
            <span className="count-badge">{getSortedSongs(roleDetail.songs).length} songs</span>
          </h2>
          
          <div className="tracks-table">
            <div className="tracks-header">
              <span className="col-play"></span>
              <span className="col-name sortable" onClick={() => handleSort('name')}>
                Song {sortConfig.field === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-artist sortable" onClick={() => handleSort('artist')}>
                Artist {sortConfig.field === 'artist' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-bpm sortable" onClick={() => handleSort('bpm')}>
                BPM {sortConfig.field === 'bpm' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-plays sortable" onClick={() => handleSort('play_count')}>
                Plays {sortConfig.field === 'play_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-roles">Roles</span>
              <span className="col-last sortable" onClick={() => handleSort('last_played')}>
                Last Played {sortConfig.field === 'last_played' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
            </div>
            {getSortedSongs(roleDetail.songs).map((song, index) => (
              <div key={song.id} className="track-row">
                <span className="col-play">
                  <button
                    className="play-btn"
                    onClick={() => handlePlayPause(song.spotify_id, getSortedSongs(roleDetail.songs), 'roleDetail')}
                  >
                    {currentPlayingId === song.spotify_id && isPlaying ? '⏸' : '▶'}
                  </button>
                </span>
                <span className="col-name">{song.name}</span>
                <span className="col-artist">{(song.artist_names || []).join(', ')}</span>
                <span className="col-bpm">
                  <span className="bpm-badge">{song.bpm || '?'}</span>
                </span>
                <span className="col-plays">{song.play_count || 0}</span>
                <span className="col-roles">
                  {(song.all_roles || []).map(r => (
                    <span
                      key={r}
                      className="role-tag"
                      style={{ backgroundColor: ROLE_COLORS[r] || '#666' }}
                    >
                      {r}
                    </span>
                  ))}
                </span>
                <span className="col-last">
                  {song.last_played
                    ? new Date(song.last_played).toLocaleDateString()
                    : 'Never'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* COLLECTIONS OVERVIEW */}
      {view === 'collections' && (
        <div className="collections-view">
          {['role', 'bpm_bucket', 'ride_type', 'special'].map(category => {
            const catCollections = collections.filter(c => c.category === category);
            if (catCollections.length === 0) return null;
            
            const categoryNames = {
              role: '🎯 Role Playlists',
              bpm_bucket: '🎵 BPM Buckets',
              ride_type: '🚴 Ride Types',
              special: '✨ Special Collections'
            };
            
            return (
              <div key={category} className="category-section">
                <h3>{categoryNames[category]}</h3>
                <div className="collections-grid">
                  {catCollections.map(c => (
                    <div 
                      key={c.id} 
                      className="collection-card"
                      onClick={() => viewCollectionDetail(c.id)}
                    >
                      <div className="collection-name">{c.name}</div>
                      <div className="collection-stats">
                        <span>{c.track_count} tracks</span>
                        {c.linked_songs > 0 && (
                          <span className="linked">{c.linked_songs} in DB</span>
                        )}
                      </div>
                      {c.bpm_min && (
                        <div className="collection-bpm">
                          {c.bpm_min}-{c.bpm_max} BPM
                        </div>
                      )}
                      {c.role && (
                        <span 
                          className="role-tag" 
                          style={{ backgroundColor: ROLE_COLORS[c.role] || '#666' }}
                        >
                          {c.role}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Dash Playlists Section */}
          {dashPlaylists.length > 0 && (
            <div className="category-section">
              <h3>🔜 Coming Soon / Staging</h3>
              <div className="collections-grid">
                {dashPlaylists.map(dp => (
                  <div 
                    key={dp.id} 
                    className="collection-card"
                    onClick={() => viewDashDetail(dp.id)}
                  >
                    <div className="collection-name">{stripDashes(dp.name)}</div>
                    <div className="collection-stats">
                      <span>{dp.track_count || dp.total_songs} tracks</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* DASH PLAYLIST DETAIL */}
      {view === 'dashDetail' && dashDetail && (
        <div className="detail-view collection-detail">
          <div className="detail-header">
            <button className="back-btn" onClick={() => setView('collections')}>
              ← Back to Collections
            </button>
          </div>
          <h2>
            {stripDashes(dashDetail.playlist.name)}
            <span className="count-badge">{dashDetail.tracks.length} tracks</span>
          </h2>
          
          <div className="tracks-table collection-tracks">
            <div className="tracks-header">
              <span className="col-pos">#</span>
              <span className="col-name">Song</span>
              <span className="col-artist">Artist</span>
              <span className="col-bpm">BPM</span>
              <span className="col-plays">Plays</span>
              <span className="col-last">Last Played</span>
            </div>
            {dashDetail.tracks.map((track, i) => (
              <div key={i} className="track-row">
                <span className="col-pos">{track.position}</span>
                <span className="col-name">{track.track_name}</span>
                <span className="col-artist">{track.artist_name}</span>
                <span className="col-bpm">
                  {track.bpm ? <span className="bpm-badge">{track.bpm}</span> : '—'}
                </span>
                <span className="col-plays">{track.play_count || 0}</span>
                <span className="col-last">
                  {track.last_played 
                    ? new Date(track.last_played).toLocaleDateString()
                    : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BY POSITION VIEW */}
      {view === 'byPosition' && byPosition && (
        <div className="detail-view">
          <h2>📍 Songs By Position</h2>
          <div className="positions-list">
            {Object.entries(byPosition).map(([key, data]) => (
              <div key={key} className="position-group">
                <div 
                  className="position-header-row"
                  onClick={() => togglePosition(key)}
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', marginBottom: expandedPositions[key] ? '0' : '8px' }}
                >
                  <span style={{ fontSize: '1.1em', fontWeight: 600 }}>
                    {expandedPositions[key] ? '▼' : '▶'} {data.label}
                  </span>
                  <span className="count-badge">{data.songs.length} songs</span>
                </div>
                {expandedPositions[key] && (
                  <div className="tracks-table" style={{ marginBottom: '16px' }}>
                    <div className="tracks-header">
                      <span className="col-name">Song</span>
                      <span className="col-artist">Artist</span>
                      <span className="col-bpm">BPM</span>
                      <span className="col-plays">× in Position</span>
                      <span className="col-last">Last Played</span>
                    </div>
                    {data.songs.map((song, i) => (
                      <div key={song.id || i} className="track-row">
                        <span className="col-name">{song.name}</span>
                        <span className="col-artist">{song.artist_name}</span>
                        <span className="col-bpm">
                          {song.bpm ? <span className="bpm-badge">{song.bpm}</span> : '—'}
                        </span>
                        <span className="col-plays">{song.position_count}</span>
                        <span className="col-last">
                          {song.last_played 
                            ? new Date(song.last_played).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* COLLECTION DETAIL */}
      {view === 'collectionDetail' && collectionDetail && (
        <div className="detail-view collection-detail">
          <div className="detail-header">
            <button className="back-btn" onClick={() => setView('collections')}>
              ← Back to Collections
            </button>
            <div className="filter-checkboxes">
              <label className="hide-recent-checkbox">
                <input 
                  type="checkbox" 
                  checked={hideRecent} 
                  onChange={(e) => setHideRecent(e.target.checked)}
                />
                Hide Recent
              </label>
              {user?.isInstructor && (
                <label className="hide-recent-checkbox hide-colorado">
                  <input 
                    type="checkbox" 
                    checked={hideAfterColorado} 
                    onChange={(e) => setHideAfterColorado(e.target.checked)}
                  />
                  Colorado: Songs not played since 2022
                </label>
              )}
              {user?.isInstructor && (
                <label className="hide-recent-checkbox one-hit-wonders">
                  <input 
                    type="checkbox" 
                    checked={showOneHitWonders} 
                    onChange={(e) => setShowOneHitWonders(e.target.checked)}
                  />
                  Colorado: Songs played only once since 2022
                </label>
              )}
            </div>
          </div>
          <h2>
            {collectionDetail.collection.name}
            <span className="count-badge">{getSortedCollectionTracks(collectionDetail.tracks).length} tracks</span>
          </h2>
          {collectionDetail.collection.bpm_min && (
            <p className="collection-bpm-range">
              BPM Range: {collectionDetail.collection.bpm_min}-{collectionDetail.collection.bpm_max}
            </p>
          )}
          
          <div className="tracks-table collection-tracks">
            <div className="tracks-header">
              <span className="col-play"></span>
              <span className="col-pos sortable" onClick={() => handleSort('position')}>
                # {sortConfig.field === 'position' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-name sortable" onClick={() => handleSort('name')}>
                Song {sortConfig.field === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-artist sortable" onClick={() => handleSort('artist')}>
                Artist {sortConfig.field === 'artist' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-bpm sortable" onClick={() => handleSort('bpm')}>
                BPM {sortConfig.field === 'bpm' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-plays sortable" onClick={() => handleSort('play_count')}>
                Plays {sortConfig.field === 'play_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
              <span className="col-last sortable" onClick={() => handleSort('last_played')}>
                Last Played {sortConfig.field === 'last_played' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </span>
            </div>
            {getSortedCollectionTracks(collectionDetail.tracks).map((track, i) => (
              <div key={i} className={`track-row ${track.song_id ? '' : 'not-in-db'}`}>
                <span className="col-play">
                  {track.spotify_track_id && (
                    <button
                      className="play-btn"
                      onClick={() => handlePlayPause(track.spotify_track_id, getSortedCollectionTracks(collectionDetail.tracks), 'collectionDetail')}
                    >
                      {currentPlayingId === track.spotify_track_id && isPlaying ? '⏸' : '▶'}
                    </button>
                  )}
                </span>
                <span className="col-pos">{track.position}</span>
                <span className="col-name">{track.track_name}</span>
                <span className="col-artist">{track.artist_name}</span>
                <span className="col-bpm">
                  {track.bpm ? <span className="bpm-badge">{track.bpm}</span> : '—'}
                </span>
                <span className="col-plays">{track.play_count || 0}</span>
                <span className="col-last">
                  {track.last_played
                    ? new Date(track.last_played).toLocaleDateString()
                    : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Library;
